'use client';

import classnames from 'classnames';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { ButtonStyle } from '../button/ButtonStyle.enum';
import TwoButtonComponent from '../button/TwoButtonComponent';
import { stringFormatJsx } from '../helpers/string-helper';
import useCookies from '../hooks/useCookies';
import { IconLock16 } from '../icons/ic-lock';

import { CookiePreferencesModal } from './CookiePreferencesModal';
import Modal, { type ModalProps } from './Modal';

type CookiesModalProps = ModalProps & {
    // When the banner is conditionally rendered by its parent (as the modal-conditional-render
    // lint rule requires), the internal preferences modal would unmount together with the banner.
    // Pass this to let the parent own the preferences modal instead.
    onShowCookiePreferences?: () => void;
    // Where the privacy policy lives. Apps served under customer domains — the movie landing pages
    // and the cinema website — have no `/data-protection` route of their own, so the default would
    // link the visitor into a 404 from inside the consent banner (CIN-14630).
    privacyPolicyUrl?: string;
};

const CookiesModal: React.FC<CookiesModalProps> = ({
    onShowCookiePreferences,
    privacyPolicyUrl = '/data-protection',
    ...props
}) => {
    const t = useTranslations();

    const [cookiePreferencesIsOpen, setCookiePreferencesIsOpen] = useState<boolean>(false);

    const { acceptAllOptionalCookies, rejectAllOptionalCookies } = useCookies();

    function onShowCookiePreferencesClick() {
        props.setModalIsOpen?.(false);
        if (onShowCookiePreferences) {
            onShowCookiePreferences();
            return;
        }
        setCookiePreferencesIsOpen(!cookiePreferencesIsOpen);
    }

    function saveCookiesPreferenceAndClose() {
        acceptAllOptionalCookies();
        props.setModalIsOpen?.(false);
    }

    function rejectAllOptionalCookiesAndClose() {
        rejectAllOptionalCookies();
        props.setModalIsOpen?.(false);
    }

    return (
        <>
            {cookiePreferencesIsOpen && (
                <CookiePreferencesModal
                    modalIsOpen={cookiePreferencesIsOpen}
                    setModalIsOpen={setCookiePreferencesIsOpen}
                    onBackClick={() => props.setModalIsOpen?.(true)}
                    privacyPolicyUrl={privacyPolicyUrl}
                />
            )}

            <Modal
                {...props}
                onClose={null}
                className={classnames('!container max-w-full !p-24 !min-h-min !mt-auto')}
                showBackground={false}
                showCloseButton={false}
                closeable={false}
                enableOutsideActions>
                <div className="flex flex-col">
                    <div className="flex flex-col lg:flex-row flex-auto lg:space-x-24 space-y-16 lg:space-y-0">
                        <div className="flex flex-col flex-auto lg:w-1/2 space-y-6">
                            <div className="flex flex-row space-x-12 items-center">
                                <span className="text-body-bold-16">{t('common.cookies-modal.your-privacy')}</span>
                                <IconLock16 className="flex-none text-[var(--primary-dark-color)]" />
                            </div>
                            <span className="text-body-12">
                                {stringFormatJsx(
                                    t('common.cookies-modal.description', {
                                        0: '{0}'
                                    }),
                                    <Link
                                        className="underline"
                                        href={privacyPolicyUrl}>
                                        {t('common.cookies-modal.privacy-policy')}
                                    </Link>
                                )}
                            </span>
                        </div>
                        <div className="flex flex-col lg:w-1/2 lg:justify-center space-y-16">
                            <TwoButtonComponent
                                primaryLabel={t('common.cookies-modal.cookie-preferences')}
                                primaryAction={onShowCookiePreferencesClick}
                                primaryButtonProps={{
                                    className: classnames('h-48'),
                                    style: ButtonStyle.outlined_gray_on_white_bg
                                }}
                                secondaryLabel={t('common.cookies-modal.accept')}
                                secondaryAction={saveCookiesPreferenceAndClose}
                                secondaryButtonProps={{
                                    className: classnames('h-48'),
                                    style: ButtonStyle.default_on_white_bg
                                }}
                                stretched
                            />
                            <span
                                className={classnames(
                                    'flex clickable-element underline text-[var(--on-surface-color)] text-body-12'
                                )}
                                onClick={rejectAllOptionalCookiesAndClose}>
                                {t('common.cookies-modal.decline-and-continue')}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CookiesModal;
